import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["item"]
  static values = { url: String }

  connect() {
    this.draggedItem = null

    // Listen for drops from notes onto sections
    this.element.addEventListener("dragover", this.handleContainerDragOver)
    this.element.addEventListener("drop", this.handleContainerDrop)
    this.element.addEventListener("dragleave", this.handleContainerDragLeave)
  }

  disconnect() {
    this.element.removeEventListener("dragover", this.handleContainerDragOver)
    this.element.removeEventListener("drop", this.handleContainerDrop)
    this.element.removeEventListener("dragleave", this.handleContainerDragLeave)
  }

  // Section drag start (for reordering sections)
  itemTargetConnected(item) {
    item.setAttribute("draggable", "true")
    item.addEventListener("dragstart", this.handleDragStart)
    item.addEventListener("dragend", this.handleDragEnd)
  }

  itemTargetDisconnected(item) {
    item.removeEventListener("dragstart", this.handleDragStart)
    item.removeEventListener("dragend", this.handleDragEnd)
  }

  handleDragStart = (event) => {
    this.draggedItem = event.target.closest("[data-sections-sortable-target='item']")
    this.draggedItem.classList.add("dragging")
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", this.draggedItem.id)
    event.dataTransfer.setData("application/x-section", this.draggedItem.id)
  }

  handleDragEnd = () => {
    if (this.draggedItem) {
      this.draggedItem.classList.remove("dragging")
    }
    this.clearDragStyles()
    this.draggedItem = null
  }

  // Container-level handlers for receiving drops (notes or sections)
  handleContainerDragOver = (event) => {
    // Must preventDefault to allow drop
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"

    const target = event.target.closest("[data-sections-sortable-target='item']")
    const isNote = event.dataTransfer.types.includes("application/x-note")
    const isSection = event.dataTransfer.types.includes("application/x-section")

    if (!target) return

    this.clearDragStyles()

    if (isNote) {
      target.classList.add("drag-over-note")
    } else if (isSection && target !== this.draggedItem) {
      const rect = target.getBoundingClientRect()
      const midpoint = rect.top + rect.height / 2
      if (event.clientY < midpoint) {
        target.classList.add("drag-over-top")
      } else {
        target.classList.add("drag-over-bottom")
      }
    }
  }

  handleContainerDragLeave = (event) => {
    if (!this.element.contains(event.relatedTarget)) {
      this.clearDragStyles()
    }
  }

  handleContainerDrop = (event) => {
    event.preventDefault()

    const target = event.target.closest("[data-sections-sortable-target='item']")
    const draggedData = event.dataTransfer.getData("text/plain")

    if (!target) {
      this.clearDragStyles()
      return
    }

    // Check if a note is being dropped
    const noteMatch = draggedData.match(/^note_(\d+)$/)
    if (noteMatch) {
      const noteId = noteMatch[1]
      const sectionId = target.dataset.sectionId
      if (sectionId) {
        this.moveNoteToSection(noteId, sectionId)
      }
      this.clearDragStyles()
      return
    }

    // Section reordering
    if (!this.draggedItem || target === this.draggedItem) {
      this.clearDragStyles()
      return
    }

    const rect = target.getBoundingClientRect()
    const midpoint = rect.top + rect.height / 2

    if (event.clientY < midpoint) {
      target.parentNode.insertBefore(this.draggedItem, target)
    } else {
      target.parentNode.insertBefore(this.draggedItem, target.nextSibling)
    }

    this.clearDragStyles()
    this.saveOrder()
  }

  clearDragStyles() {
    this.itemTargets.forEach(item => {
      item.classList.remove("drag-over-top", "drag-over-bottom", "drag-over-note")
    })
  }

  moveNoteToSection(noteId, sectionId) {
    fetch(`/notes/${noteId}/move_to_section`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
      },
      body: JSON.stringify({ section_id: sectionId })
    })
    .then(response => {
      if (response.ok) {
        window.location.reload()
      }
    })
  }

  saveOrder() {
    const sectionIds = this.itemTargets.map(item => item.dataset.sectionId).filter(id => id)

    fetch(this.urlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
      },
      body: JSON.stringify({ section_ids: sectionIds })
    })
  }
}
